import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { Equals, IsNotEmpty, IsPhoneNumber } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { NavigateFunction } from 'react-router-dom';

import { SignUpDto } from '../../../../shared/application/interfaces/pegasus-io.gateway';
import { UrlUtils } from '../../../../shared/application/utils/url';
import {
  IsFullname,
  IsStrongPassword,
  Match,
  ObservableForm,
} from '../../../../shared/presentation/forms/BaseForm';
import { MicrofrontendViewModel } from '../../../../shared/presentation/Microfrontend.vm';
import { CheckInvitationCollaboration } from '../../application/collaborations/check-invitation.collaboration';
import { SignUpCollaboration } from '../../application/collaborations/signup.collaboration';

class SignUpForm {
  @IsFullname()
  name = '';

  @IsPhoneNumber('BR', { message: 'Telefone inválido' })
  phoneNumber = '';

  @IsNotEmpty({ message: 'Campo obrigatório' })
  @IsStrongPassword()
  password = '';

  @IsNotEmpty({ message: 'Campo obrigatório' })
  @Match((o) => o.password, { message: 'As senhas devem ser iguais' })
  passwordConfirmation = '';

  @Equals(true, { message: 'Você deve aceitar os termos de uso' })
  isTermsAccepted = false;

  @IsNotEmpty({ message: 'Campo obrigatório' })
  token = '';
}

@ViewModel()
export class SignUpViewModel implements IReactiveModel {
  @observable form = new ObservableForm(SignUpForm);
  @observable email = '';
  @observable isLoadingInvitationDetails = true;
  @observable isInviteValid = true;
  @observable isSubmitting = false;
  @observable errorMessage = '';
  @observable withHelpCenterLink = false;

  navigate: NavigateFunction;
  private executeRecaptcha: (action: string) => Promise<string>;

  constructor(
    private signUpcollaboration: SignUpCollaboration,
    private checkInvitationCollaboration: CheckInvitationCollaboration,
    private microfrontendViewModel: MicrofrontendViewModel,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit({ navigate }) {
    try {
      this.navigate = navigate;
      const token = UrlUtils.getQueryParam('token');

      if (!token) {
        setTimeout(() => {
          navigate('/');
        }, 100);
        return;
      }

      const result = await this.checkInvitationCollaboration.run({
        token,
      });

      if (this.microfrontendViewModel.account) {
        setTimeout(() => {
          navigate('/');
        }, 100);

        return;
      }

      this.form.setValue('token', token);
      this.email = result.data.email;

      this.isLoadingInvitationDetails = false;
    } catch (error) {
      console.error(error);
      this.isInviteValid = false;
      this.errorMessage = 'Ocorreu um erro ao tentar validar o convite.';
    }
  }

  @action
  onChanges({ executeRecaptcha }) {
    this.executeRecaptcha = executeRecaptcha;
  }

  async signUp() {
    try {
      if (this.form.hasError) {
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';
      this.withHelpCenterLink = false;

      if (!this.executeRecaptcha) {
        throw new Error('Execute recaptcha not yet available');
      }

      const googleRecaptchaResponse = await this.executeRecaptcha('SignIn');

      const dto: SignUpDto = {
        name: this.form.values.name?.trim(),
        phoneNumber: this.form.values.phoneNumber.replace(/\D/g, ''),
        password: this.form.values.password,
        passwordConfirmation: this.form.values.passwordConfirmation,
        token: this.form.values.token,
        googleRecaptchaResponse,
      };

      await this.signUpcollaboration.run(dto);
      await this.microfrontendViewModel.identifyCurrentAccount();

      const redirectUrlParam = new URLSearchParams(window.location.search).get('redirectUrl');

      if (redirectUrlParam) {
        try {
          const allowedRedirectDomains = ['descomplica.com.br', 'descomplica.io', 'localhost'];
          const url = decodeURIComponent(redirectUrlParam);
          const urlBaseDomain = new URL(url).hostname.replace(/^[^.]+\./g, '');

          if (allowedRedirectDomains.includes(urlBaseDomain)) {
            window.location.href = url;
            return;
          }
        } catch (error) {
          this.navigate('/');
          return;
        }
      }

      this.navigate('/');
    } catch (error) {
      if (error.response.data.identifier === 'InvalidRecaptcha') {
        this.errorMessage =
          'Desculpe, não conseguimos verificar a integridade do seu acesso de forma automática. Por favor, tente novamente mais tarde.';
        this.withHelpCenterLink = true;
        return;
      }

      console.error(error);
      this.errorMessage = 'Ocorreu um erro ao tentar criar sua conta.';
    } finally {
      this.isSubmitting = false;
    }
  }

  goToSignIn(): void {
    const token = UrlUtils.getQueryParam('token');
    this.navigate(`/signin${token ? `?token=${token}` : ''}`);
  }
}
